/**
 * Centralized API base-URL configuration.
 *
 * `API_BASE_URL` — the API root the SPA talks to (the localhost fallback
 *   includes the `/api` path). Used as the axios `baseURL` in services/api.ts
 *   and by the handful of direct fetch()/link URLs that don't go through the
 *   shared axios instance.
 *
 * `API_ORIGIN` — the same server without the trailing `/api`, for callers that
 *   build their own `/api/...` path (e.g. the profile-picture proxy URLs).
 *
 * Prefer the shared `api` axios instance (services/api.ts) for data calls;
 * reach for these constants only when you need a raw URL string.
 */
const resolveApiBase = (): string => {
  const configured = import.meta.env.VITE_API_URL;
  if (configured) return configured;
  // No explicit override → talk to the SAME origin that served the SPA, so a
  // single build works across every host (office/patient × spectorlab /
  // analyzehealthcare). In dev the Vite proxy forwards /api → the backend; in
  // prod the ALB routes each host's /api/* to the backend. (VITE_API_URL still
  // wins when set, preserving existing per-env builds.)
  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}/api`;
  }
  return 'http://localhost:5000/api';
};

export const API_BASE_URL = resolveApiBase();

export const API_ORIGIN = API_BASE_URL.replace(/\/api$/, '');
